import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";
import { cn } from "../../utils/cn";
import { computeTooltipPosition } from "../ui/Tooltip";

// Margen para pasar el puntero del botón al globo sin que se cierre (WCAG 1.4.13).
const HOVER_CLOSE_GRACE_MS = 100;

/**
 * Globo de una ayuda FIJADA (abierta con clic o Enter). Los atajos de teclado de
 * la vista la cuentan como un overlay abierto, igual que un menú: una tecla no
 * actúa por detrás de ella (Escape la cierra primero).
 */
export const PINNED_PANEL_HINT_SELECTOR = "[data-panel-hint-pinned]";

/**
 * Ámbito de las ayudas: cuando su valor cambia, las abiertas adentro se cierran.
 * El panel pasa su conversación y su sub-vista: una ayuda fijada que no se
 * desmonta con el cambio (la de la cabecera) no queda abierta encima del pedido
 * o del alta que se acaba de abrir.
 */
export const PanelHintScope = createContext(null);

function viewportSize() {
  const root = document.documentElement;
  return { width: root.clientWidth || window.innerWidth, height: root.clientHeight || window.innerHeight };
}

/**
 * Ayuda (?) del panel de incidencias: se comporta como ui/InfoHint (se abre con
 * hover, foco o clic; el clic la fija; se cierra con Escape o tocando fuera),
 * pero el globo va en un PORTAL con posición fija, como ui/Tooltip.
 *
 * Por qué no ui/InfoHint tal cual: su globo es `absolute`, de 320 px y queda
 * montado aunque esté oculto, anclado al ícono. Dentro de la lista con scroll
 * del panel (280 a 440 px) ese globo invisible agrandaba el área desplazable
 * (scroll horizontal permanente) y, abierto, quedaba cortado por el borde de la
 * columna o de la pantalla. En el portal no ocupa lugar en el panel y se acomoda
 * dentro del viewport. No se toca el primitivo compartido.
 *
 * Accesibilidad: el texto vive siempre en un <span hidden> enlazado por
 * `aria-describedby` (el lector lo anuncia al llegar al botón, sin abrirlo); la
 * copia visual del portal es `aria-hidden`. Debajo de `md` el botón mide 44 px.
 * Escape se escucha en captura y se consume: cierra la ayuda sin cerrar además
 * la hoja de tablet que contiene al panel.
 *
 * Como el globo vive fuera del panel, no se oculta solo con su ancla: se cierra
 * cuando el ícono deja de verse (hoja cerrada, columna plegada, otra pantalla en
 * móvil, o la lista lo sacó de la vista) y cuando cambia el ámbito
 * (PanelHintScope). Fijada, en cambio, sigue abierta aunque el foco se vaya.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label - Texto de la ayuda.
 * @param {string} props.srLabel - Nombre accesible del botón.
 * @param {'start'|'end'} [props.align] - Borde del botón con el que se alinea el globo.
 * @param {string} [props.className] - Clases del contenedor.
 */
export default function PanelHint({ label, srLabel, align = "start", className }) {
  const descriptionId = useId();
  const buttonRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Abierta con clic: sacar el puntero o perder el foco no la cierra (se puede
  // leer un texto largo sin perseguir el cursor).
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
    setPinned(false);
    setPosition(null);
  }, [clearCloseTimer]);

  const scheduleClose = () => {
    if (pinned) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(close, HOVER_CLOSE_GRACE_MS);
  };

  const show = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    const bubble = bubbleRef.current;
    if (!button || !bubble) return;
    const viewport = viewportSize();
    const triggerRect = button.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const next = computeTooltipPosition({
      triggerRect,
      panelWidth: bubbleRect.width,
      panelHeight: bubbleRect.height,
      side: "bottom",
      align: align === "end" ? "end" : "start",
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
    });
    setPosition((previous) =>
      previous && previous.top === next.top && previous.left === next.left ? previous : next,
    );
  }, [align]);

  // Se mide antes de pintar: el globo aparece ya en su lugar, sin saltos.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, label, updatePosition]);

  // Otro ámbito (otra sub-vista u otra conversación del panel): se cierra.
  const scope = useContext(PanelHintScope);
  const scopeRef = useRef(scope);
  useEffect(() => {
    if (Object.is(scopeRef.current, scope)) return;
    scopeRef.current = scope;
    close();
  }, [scope, close]);

  // Mientras está abierta, el ícono tiene que seguir a la vista. Un `hidden` en
  // un ancestro (hoja cerrada, columna plegada, pantalla de móvil) o el recorte
  // de un contenedor con scroll lo sacan de la intersección con el viewport: el
  // globo del portal quedaría flotando sin su ancla. Sin IntersectionObserver
  // (jsdom) no hay observación; el cierre por ámbito y por desmontaje sigue.
  useEffect(() => {
    const button = buttonRef.current;
    if (!open || !button || typeof window.IntersectionObserver !== "function") return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      const latest = entries[entries.length - 1];
      if (latest && !latest.isIntersecting) close();
    });
    observer.observe(button);
    return () => observer.disconnect();
  }, [open, close]);

  // Mientras está abierta: se reacomoda con el scroll (de cualquier contenedor)
  // y el resize, y se cierra con Escape o con un toque fuera.
  useEffect(() => {
    if (!open) return undefined;
    let frame = null;
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || bubbleRef.current?.contains(event.target)) return;
      close();
    };
    window.addEventListener("scroll", scheduleUpdate, { capture: true, passive: true });
    window.addEventListener("resize", scheduleUpdate);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("scroll", scheduleUpdate, { capture: true });
      window.removeEventListener("resize", scheduleUpdate);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [open, updatePosition, close]);

  // Al desmontar, sin temporizadores colgados.
  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  return (
    <span className={cn("inline-flex", className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={srLabel}
        aria-expanded={open}
        aria-describedby={descriptionId}
        onClick={() => {
          if (pinned) {
            close();
            return;
          }
          setPinned(true);
          show();
        }}
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onFocus={show}
        onBlur={() => {
          if (!pinned) close();
        }}
        // 44 px táctiles debajo de md; fg-muted (no fg-subtle): el ícono es
        // información y necesita 3:1 contra el fondo.
        className={cn(
          "inline-flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 rounded-full",
          "text-fg-muted hover:text-fg transition-colors cursor-pointer",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        )}
      >
        <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      <span id={descriptionId} hidden>
        {label}
      </span>
      {open &&
        createPortal(
          <span
            ref={bubbleRef}
            role="tooltip"
            aria-hidden="true"
            data-panel-hint-pinned={pinned ? "" : undefined}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            style={position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: "hidden" }}
            className={cn(
              "fixed z-[10000] block w-[min(20rem,calc(100vw-1rem))] rounded-md border border-border bg-bg p-2.5 shadow-lg",
              "text-xs font-normal normal-case tracking-normal leading-relaxed text-fg-muted",
            )}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
